import { createSelector } from 'reselect';

import { USER_STATUS } from 'constants/UserConstants';

import { getCurrentUserIsAdmin } from 'modules/users/selectors/currentUser';
import { humanize } from 'utils/global';
import { getProps } from 'utils/selectors';

/** @return {import("reducers/userReducer").LegacyUserReducer} */
const getUsersState = state => state.users;

export const getUsers = createSelector(getUsersState, users => users.data);

export const getUsersContent = createSelector(getUsers, data => data.content);

export const getUsersContentPage = createSelector(getUsers, data => data.page);

const getUsersExist = createSelector(getUsers, data => data.exist);

export const isUsedUsername = createSelector(getUsersExist, ({ isUsed }) => isUsed);

export const checkUsernameIsFetching = createSelector(getUsersExist, ({ isFetching }) => isFetching);

export const getUsersIsFetching = createSelector(getUsersState, users => users.isFetching);

export const getUsersApiError = createSelector(getUsersState, users => users.apiError);

export const getUsersFilter = createSelector(
  getUsersState,
  getCurrentUserIsAdmin,
  ({ filters: { rolesNot, ...filters } }, isAdmin) => ({
    // In admin case - We remove all roles limitation
    rolesNot: isAdmin ? [] : rolesNot,
    ...filters,
  })
);

export const getRolesFilters = createSelector(getUsersFilter, filters => filters.roles);

const getStatusFilters = createSelector(getUsersFilter, filters => filters.status);

export const hasDisplayInactiveFilter = createSelector(getStatusFilters, status =>
  status.includes(USER_STATUS.INACTIVE));

export const countFilters = createSelector(getRolesFilters, roles => roles.length);

const getFormattedRoles = createSelector(
  getProps,
  getRolesFilters,
  (intl, roles) =>
    roles.length > 0 && {
      id: 'roles',
      name: intl.formatMessage({ id: 'roles', defaultMessage: 'Roles' }),
      values: roles.map(role => ({
        value: role,
        name: intl.formatMessage({ id: `role.${role}`, defaultMessage: humanize(role) }),
      })),
    }
);

export const getFormattedFilters = createSelector(getFormattedRoles, roles => [...(roles ? [roles] : [])]);

export const getUserById = createSelector(getUsersContent, getProps, (users, id) => users?.[id]);

export const getUsersByIds = createSelector(
  getUsersContent,
  getProps,
  /** @param {string[]} [ids=[]]  */ (users, ids = []) => ids.map(id => users?.[id]).filter(Boolean)
);

const getSelectedUserId = createSelector(getUsersState, state => state.selectedUserId);

export const getSelectedUser = createSelector([getUsersContent, getSelectedUserId], (users, id) => users?.[id]);

export const isUserValidating = createSelector(getUsersState, ({ isValidating }) => isValidating);

const getAssigned = createSelector(getUsers, data => data.assigned);

export const getAssignedUsersIds = createSelector(getAssigned, assigned => assigned.ids);

export const getAssignedUsersLinks = createSelector(getAssigned, assigned => assigned.links);

export const getAssignedUsersIsFetching = createSelector(getAssigned, assigned => assigned.isFetching);

export const getAssignedUsersApiError = createSelector(getAssigned, assigned => assigned.apiError);

const getUiState = createSelector(getUsersState, state => state.ui);

export const detailsPageIsReady = createSelector(getUiState, state => state.details.ready);

export const getEmailStatus = createSelector(getSelectedUser, ({ emailStatus }) => emailStatus);
